/**
 * WebTokenProvider — Web token provider.
 * Delegates to OAuthTokenStore (in-memory with auto-refresh).
 */

import type { OAuthProviderConfig, StoredTokens } from './types'
import type { TokenProvider, TokenRefreshResult } from '../TokenProvider'

export class WebTokenProvider implements TokenProvider {
    private providerConfig: OAuthProviderConfig | null = null

    public async getAccessToken (): Promise<string> {
        const { oauthTokenStore } = await import('./OAuthTokenStore')
        const token = await oauthTokenStore.getAccessToken()
        if (!token) throw new Error('No access token — user not authenticated')
        return token
    }

    public async forceRefresh (options?: { timeoutMs?: number }): Promise<TokenRefreshResult> {
        const { oauthTokenStore } = await import('./OAuthTokenStore')
        return oauthTokenStore.forceRefresh(options)
    }

    public async revoke (): Promise<void> {
        const { oauthTokenStore } = await import('./OAuthTokenStore')
        await oauthTokenStore.revokeRefreshToken()
    }

    public async isAuthenticated (): Promise<boolean> {
        const { oauthTokenStore } = await import('./OAuthTokenStore')
        return oauthTokenStore.isAuthenticated()
    }

    public storeTokens (tokens: StoredTokens): void {
        // tslint:disable-next-line: no-var-requires
        const { oauthTokenStore } = require('./OAuthTokenStore')
        oauthTokenStore.setTokens(tokens)
    }

    public getProviderConfig (): OAuthProviderConfig | null {
        return this.providerConfig
    }

    public setProviderConfig (config: OAuthProviderConfig): void {
        this.providerConfig = config
        // tslint:disable-next-line: no-var-requires
        const { oauthTokenStore } = require('./OAuthTokenStore')
        oauthTokenStore.setProviderConfig({
            tokenEndpoint: config.tokenEndpoint,
            clientId: config.clientId,
        })
    }

    public async clear (): Promise<void> {
        const { oauthTokenStore } = await import('./OAuthTokenStore')
        oauthTokenStore.clear()
        this.providerConfig = null
    }
}
